import { useEffect } from "react";

import {
  adoptOnboardingAccount,
  getCurrentAccountId,
  saveAccountLanguages,
  setCurrentAccountId,
  trackEvent,
} from "@/lib/api";
import {
  clearOnboardingAuthDraft,
  clearOnboardingHandoffToken,
  readOnboardingHandoffToken,
} from "@/lib/onboarding-auth";
import {
  recoverLinkedAuthCallbackAccount,
  resolveOnboardingAuthCallbackRecovery,
} from "@/lib/onboarding-auth-callback-recovery";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";
import {
  readSupabaseAuthErrorFromUrl,
  stripSupabaseAuthCallbackFromUrl,
} from "@/lib/supabase-session";

export function useLinkedAuthCallbackRecovery({
  authCallbackErrorHandledRef,
  authDraftContextKey = "",
  email = "",
  getCurrentSupabaseUser,
  isSignInSurface = false,
  languagePayload = {},
  languageStepComplete = false,
  normalizedStoredEmail = "",
  onAuthenticated,
  setFormError,
  setVerificationError,
  status = "",
  userMode = "",
} = {}) {
  useEffect(() => {
    if (authCallbackErrorHandledRef.current || typeof window === "undefined") {
      return undefined;
    }

    const authError = readSupabaseAuthErrorFromUrl(window.location.href);
    if (!authError) {
      return undefined;
    }

    authCallbackErrorHandledRef.current = true;
    const nextUrl = stripSupabaseAuthCallbackFromUrl(window.location.href);
    if (window.history?.replaceState) {
      window.history.replaceState(window.history.state, "", nextUrl);
    }

    setFormError("");
    setVerificationError("");

    const client = getSupabaseBrowserClient();
    let cancelled = false;

    async function resolveAuthError() {
      let recoveredAccountId = "";
      try {
        recoveredAccountId = String(await recoverLinkedAuthCallbackAccount({
          client: !isSignInSurface ? client : null,
          languageStepComplete,
          handoffToken: readOnboardingHandoffToken(),
          currentAccountId: getCurrentAccountId(),
          email: email || normalizedStoredEmail,
          languagePayload,
          userMode,
          getCurrentSupabaseUser,
          adoptOnboardingAccount,
          saveAccountLanguages,
          setCurrentAccountId,
          clearOnboardingHandoffToken,
        }) || "").trim();
      } catch {
        recoveredAccountId = "";
      }

      const recovery = resolveOnboardingAuthCallbackRecovery({
        authError,
        status,
        isSignInSurface,
        hasRecoverableOwnedAccount: Boolean(recoveredAccountId),
      });

      if (cancelled) {
        return;
      }

      if (recovery.branch && recovery.branch !== "none") {
        trackEvent("onboarding_auth_callback_recovery", {
          branch: recovery.branch,
          error_code: String(authError.errorCode || "").trim(),
        });
      }

      if (recovery.shouldAutoRecover) {
        clearOnboardingAuthDraft(authDraftContextKey);
        onAuthenticated?.({
          userMode: "existing",
          accountId: recoveredAccountId,
          setupMode: "new",
        });
        return;
      }

      if (recovery.errorTarget === "verification") {
        setVerificationError(recovery.message);
        return;
      }
      if (recovery.errorTarget === "form") {
        setFormError(recovery.message);
      }
    }

    void resolveAuthError();

    return () => {
      cancelled = true;
    };
  }, [
    authCallbackErrorHandledRef,
    authDraftContextKey,
    email,
    getCurrentSupabaseUser,
    isSignInSurface,
    languagePayload,
    languageStepComplete,
    normalizedStoredEmail,
    onAuthenticated,
    setFormError,
    setVerificationError,
    status,
    userMode,
  ]);
}
